import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { PlaybackRateControl, type PlaybackRate } from './PlaybackRateControl';
import { useAudioPlayer } from '@/shared/stores/audioPlayer';

export interface AudioPlayerProps {
  src: string;
  duration?: number;
  showWaveform?: boolean;
  /**
   * כשמסופק — ההשמעה עוברת לסרגל הנגן הגלובלי (PersistentAudioBar) וממשיכה
   * גם אחרי סגירת ה-drawer/ניווט. בלעדיו הנגן מתנהג inline כרגיל.
   */
  title?: string;
  subtitle?: string;
}

export function AudioPlayer({ src, duration: knownDuration, showWaveform = true, title, subtitle }: AudioPlayerProps) {
  const playGlobal = useAudioPlayer((s) => s.play);

  // מצב handoff: כפתור אחד שמעביר את ההשמעה לסרגל הגלובלי המתמשך.
  if (title) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 'var(--pad-row) var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-3)' }}>
        <button
          type="button"
          className="btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          onClick={() => playGlobal({ src, title, subtitle, duration: knownDuration })}
          aria-label="play"
        >
          <Icon name="play" size={16} /> נגן הקלטה
        </button>
        <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
          מתנגן בסרגל התחתון — ממשיך גם אם תסגור או תעבור דף
        </span>
      </div>
    );
  }

  return <InlineAudioPlayer src={src} duration={knownDuration} showWaveform={showWaveform} />;
}

function InlineAudioPlayer({ src, duration: knownDuration, showWaveform = true }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(knownDuration ?? 0);
  const [failed, setFailed] = useState(false);
  const [rate, setRate] = useState<PlaybackRate>(1);

  useEffect(() => { setFailed(false); }, [src]);

  // playbackRate מתאפס כשהדפדפן טוען מקור חדש — מחילים אותו מחדש על כל
  // שינוי של src או של הבחירה, אחרת ההקלטה הבאה חוזרת ל-×1 בשקט.
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.playbackRate = rate;
  }, [rate, src]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onError = () => { setFailed(true); setPlaying(false); };
    audio.addEventListener('error', onError);

    // Some WAV/streamed sources report duration === Infinity until the file is
    // fully buffered. Nudging currentTime to the end forces the browser to
    // resolve the real duration, then we snap back to 0.
    const resolveDuration = () => {
      const d = audio.duration;
      if (Number.isFinite(d) && d > 0) {
        setDuration(d);
      } else if (d === Infinity) {
        const onceTime = () => {
          audio.removeEventListener('timeupdate', onceTime);
          if (Number.isFinite(audio.duration)) setDuration(audio.duration);
          audio.currentTime = 0;
        };
        audio.addEventListener('timeupdate', onceTime);
        audio.currentTime = 1e101; // beyond the end → browser clamps + sets duration
      } else if (knownDuration) {
        setDuration(knownDuration);
      }
    };

    const onTime = () => setProgress(audio.currentTime);
    const onEnd = () => { setPlaying(false); setProgress(0); };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', resolveDuration);
    audio.addEventListener('durationchange', resolveDuration);
    audio.addEventListener('ended', onEnd);

    return () => {
      audio.removeEventListener('error', onError);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', resolveDuration);
      audio.removeEventListener('durationchange', resolveDuration);
      audio.removeEventListener('ended', onEnd);
    };
  }, [src, knownDuration]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio || failed) return;

    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      // play() rejects on unsupported/missing source — handle it instead of
      // letting it surface as an uncaught promise error.
      audio.play().then(() => setPlaying(true)).catch(() => { setFailed(true); setPlaying(false); });
    }
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    // ב-RTL פס ההתקדמות גדל מימין לשמאל, ולכן ההתחלה היא הקצה הימני
    const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
    const ratio = rtl ? (rect.right - e.clientX) / rect.width : (e.clientX - rect.left) / rect.width;
    audio.currentTime = Math.min(Math.max(ratio, 0), 1) * duration;
  };

  const fmt = (s: number) => {
    if (!Number.isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60).toString().padStart(2, '0');
    return `${m}:${r}`;
  };

  const hasDuration = Number.isFinite(duration) && duration > 0;

  if (failed) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 'var(--pad-row) var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-3)', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
        <Icon name="info" size={14} />
        לא ניתן לנגן את ההקלטה
        <audio ref={audioRef} src={src} preload="metadata" />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 'var(--pad-row) var(--pad-card)',
        background: 'var(--surface-2)',
        borderRadius: 'var(--r-3)',
      }}
    >
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        type="button"
        onClick={toggle}
        className="btn"
        style={{ width: 40, height: 40, borderRadius: '50%', padding: 0 }}
        aria-label={playing ? 'pause' : 'play'}
      >
        <Icon name={playing ? 'pause' : 'play'} size={16} />
      </button>
      {showWaveform && (
        <div
          role="slider"
          aria-valuenow={progress}
          aria-valuemax={hasDuration ? duration : 0}
          onClick={seek}
          style={{
            flex: 1,
            height: 32,
            background: 'var(--surface)',
            borderRadius: 'var(--r-2)',
            position: 'relative',
            cursor: 'pointer',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: hasDuration ? `${(progress / duration) * 100}%` : 0,
              height: '100%',
              background: 'var(--accent)',
              opacity: 0.6,
              transition: 'width 80ms linear',
            }}
          />
        </div>
      )}
      <span dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontFamily: 'var(--font-mono)' }}>
        {hasDuration ? `${fmt(progress)} / ${fmt(duration)}` : fmt(progress)}
      </span>
      <PlaybackRateControl rate={rate} onChange={setRate} />
    </div>
  );
}
